import styled from "styled-components"
import { BREAKPOINTS } from "../../../GlobalTheme"

// Shared by the Integrations and OCPP modals (VLT-2695). The Settings page's own side sheet is about
// 256px wide, which fits a switch and a value but not a 90-key OCPP list, so these use a wider layout.

export const Layout = styled.div`
  display: flex;
  width: min(960px, calc(100vw - 48px));
  height: min(760px, calc(100dvh - 48px));

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-direction: column;
    width: calc(100vw - 24px);
    height: calc(100dvh - 24px);
  }
`

export const SideMenu = styled.div`
  width: 220px;
  flex-shrink: 0;
  padding: 28px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-right: 1px solid ${props => props.theme.colors.gray_200};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    flex-direction: row;
    overflow-x: auto;
    padding: 12px;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.colors.gray_200};
  }
`

export const SideMenuTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  margin: 0px 14px 12px;
  color: ${props => props.theme.colors.gray_900};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: none;
  }
`

// A long "coming soon" badge (fr: "Bientôt disponible") drops under its label rather than past the menu.
export const SideMenuItem = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 8px;
  text-align: left;
  white-space: nowrap;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$selected ? 700 : 500};
  color: ${props => props.$selected ? props.theme.colors.primary : props.theme.colors.gray_700};
  background-color: ${props => props.$selected ? props.theme.colors.primaryLight : 'transparent'};

  &:disabled {
    cursor: default;
    color: ${props => props.theme.colors.gray_400};
  }

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-wrap: nowrap;
  }
`

export const ComingSoonBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 8px;
  color: ${props => props.theme.colors.gray_500};
  background-color: ${props => props.theme.colors.gray_100};
`

export const Content = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 28px 32px;

  @media (max-width: ${BREAKPOINTS.phone}px) {
    padding: 20px 16px;
  }
`

export const CloseButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`

export const PanelTitle = styled.p`
  font-weight: 700;
  font-size: 20px;
  margin: 0px 0px 20px;
  padding-right: 40px;
  color: ${props => props.theme.colors.gray_900};
`

export const Section = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  padding: 20px 24px;
  margin-bottom: 16px;
`

export const SectionTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  margin: 0px 0px 12px;
  color: ${props => props.theme.colors.gray_900};
`

// Label on the left, control on the right; the label wraps before it pushes the control.
export const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px 16px;
  min-height: 40px;

  > *:first-child {
    overflow-wrap: anywhere;
  }
`

export const RowLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.gray_900};
`

export const RowValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.colors.gray_900};
  overflow-wrap: anywhere;
  text-align: right;
`

export const Divider = styled.div`
  height: 1px;
  margin: 12px 0px;
  background-color: ${props => props.theme.colors.gray_200};
`

export const FieldError = styled.p`
  font-size: 12px;
  margin: 6px 0px 0px;
  color: ${props => props.theme.colors.danger};
`

export const HelpText = styled.p`
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0px 0px;
  color: ${props => props.theme.colors.gray_500};
`

export const NumberInput = styled.input<{ $isError?: boolean }>`
  width: 120px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.$isError ? props.theme.colors.danger : props.theme.colors.gray_300};
  background-color: transparent;
  color: ${props => props.theme.colors.gray_900};
  font-size: 16px;
  font-weight: 600;
  text-align: right;
  box-sizing: border-box;
`

export const SegmentedControl = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background-color: ${props => props.theme.colors.gray_100};
`

export const Segment = styled.button<{ $selected: boolean }>`
  border: none;
  border-radius: 9px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.$selected ? props.theme.colors.white : props.theme.colors.gray_700};
  background-color: ${props => props.$selected ? props.theme.colors.dark : 'transparent'};
`

export const StatusBadge = styled.span<{ $tone: 'success' | 'danger' | 'warning' | 'neutral' }>`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.$tone === 'neutral' ? props.theme.colors.gray_500 : props.theme.colors[props.$tone]};
`

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`

export const CenteredLoader = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 80px;
`

export const MutedMessage = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
`

// ---- OCPP configuration keys ----

export const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: transparent;
  color: ${props => props.theme.colors.gray_900};
  font-size: 14px;
  box-sizing: border-box;
`

export const ParamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
`

// A modified value gets the accent border, which is what the "modified" legend entry shows.
export const ParamCard = styled.label<{ $isModified: boolean }>`
  display: block;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.$isModified ? props.theme.colors.primary : props.theme.colors.gray_200};
`

export const ParamKey = styled.span<{ $isReadonly: boolean }>`
  display: block;
  font-size: 12px;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
  color: ${props => props.$isReadonly ? props.theme.colors.gray_500 : props.theme.colors.primary};
`

export const ParamValueText = styled.span`
  display: block;
  font-size: 14px;
  overflow-wrap: anywhere;
  color: ${props => props.theme.colors.gray_900};
`

export const ParamInput = styled.input`
  width: 100%;
  padding: 0px;
  border: none;
  outline: none;
  background-color: transparent;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
`

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.colors.gray_500};
`

// Modifiable keys are labelled in the accent colour, so the legend shows that colour.
export const LegendModifiable = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 4px;
  vertical-align: middle;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
`

export const LegendModifiedSwatch = styled.span`
  display: inline-block;
  width: 16px;
  height: 10px;
  margin-right: 4px;
  vertical-align: middle;
  border-radius: 2px;
  border: 1px solid ${props => props.theme.colors.primary};
`

export const CollapseHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.colors.gray_900};
`

export const ResultRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0px;
  border-bottom: 1px solid ${props => props.theme.colors.gray_200};
`

export const Placeholder = styled.div`
  text-align: center;
  padding: 24px 8px;
`

export const PlaceholderTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  margin: 0px 0px 8px;
  color: ${props => props.theme.colors.gray_900};
`
